<style scoped lang="scss">
    .footnotes {
        font-size: 0.8em;
        ul {
            li {
                margin-top: 5px;
                list-style: none;
            }
        }
    }

    .uk-card-primary {
        color: white;
        background-color: #0f7ae5;
        padding: 40px;
        font-size: 1.2em;
        border-radius: 6px;
    }
</style>

<template>
    <div class="uk-form" v-if="application">
        <div class="uk-grid" v-if="!print">
            <div class="uk-width-9-10 uk-align-center uk-margin-large-bottom uk-card uk-card-primary">
                <p>We have completed this form for you using your previously submitted information. This form is required by our funder in order to provide you assistance. Please review the information and disclosures, and then with your agreement, sign it at the bottom. Thank you.</p>
            </div>
            <div class="uk-width-1-1 uk-margin-bottom">
                <h2 class="uk-align-center">Individual Applicant Request for Assistance and <br />
                    Duplication of Benefits<br />
                    Statement, Certification, and Subrogation Agreement</h2>
                <p>The Mountaineer Rental Assistance Program (MRAP) provides emergency assistance with rent and utility expenses and certain other expenses related to housing incurred due, directly or indirectly, to the Covid-19 outbreak to eligible renter households in West Virginia. This program is administered by the West Virginia Housing Development Fund (WVHDF) and is funded with $200 million allocated to the State of West Virginia by the US Department of the Treasury as part of Section 501 of Division N of the the Consolidated Appropriations Act, 2021, Pub. L. No. 116-260 (Dec. 27, 2020) (Section 501).</p>
                <p>The WVHDF must implement procedures to prevent any Duplication of Benefits (DOB) as required by Section 501. With this form, an applicant for MRAP assistance 1) outlines the expenses associated with a proposed activity; 2) identifies other assistance received or anticipated for the activity; 3) states the MRAP funding request; 4) certifies the accuracy of the information; and 5) <strong>agrees to repay any awarded MRAP assistance that is duplicated.</strong></p>
            </div>

        </div>
        <div class="uk-grid">
            <div class="uk-width-1-2 uk-margin-bottom">
                <TextInput
                        label="Applicant Name"
                        placeholder="Applicant Name"
                        rules="required"
                        disabled
                        :value="applicant.first_name + ' ' + applicant.last_name"
                ></TextInput>
            </div>
            <div class="uk-width-1-2 uk-margin-bottom">
                <TextInput
                        label="Applicant Address"
                        placeholder="Applicant Address"
                        rules="required"
                        disabled
                        :value="fullAddress"
                ></TextInput>
            </div>

            <div class="uk-width-1-1 uk-margin-bottom">
                <label class="uk-margin-bottom">Proposed Activity</label>
                <div v-for="type in assets.unitLedgerEntryTypes">
                    <input type="checkbox" disabled :checked="ledgerEntriesForType(type.id).length > 0" class=""/> {{ type.name }} Assistance
                </div>
            </div>

            <div class="uk-width-1-2 uk-margin-bottom">
                <label>Total Need</label>
                <TextInput
                        class="uk-width-1-1 uk-input uk-form-large"
                        placeholder="Total Need"
                        rules="required"
                        :value="requestedAmount | formatMoney"
                        disabled
                ></TextInput>
            </div>
            <div class="uk-width-1-2 uk-margin-bottom">
                <label>Total Assistance Received or Anticipated for Proposed Activity</label>
                <TextInput
                        class="uk-width-1-1 uk-input uk-form-large"
                        placeholder="Total Assistance Received or Anticipated for Proposed Activity"
                        rules="required"
                        :value="anticipatedAmount | formatMoney"
                        disabled
                ></TextInput>
            </div>

            <div class="uk-width-1-2 uk-margin-bottom">
                <label>Total of Non-Duplicative Assistance</label>
                <TextInput
                        class="uk-width-1-1 uk-input uk-form-large"
                        placeholder="Total of Non-Duplicative Assistance"
                        rules="required"
                        :value="nonDuplicatedAmount | formatMoney"
                        disabled
                ></TextInput>
            </div>
            <div class="uk-width-1-2 uk-margin-bottom">
                <label>Total Duplication of Benefits (DOB)</label>
                <TextInput
                        class="uk-width-1-1 uk-input uk-form-large"
                        placeholder="Total Duplication of Benefits (DOB)"
                        rules="required"
                        :value="duplicatedAmount | formatMoney"
                        disabled
                ></TextInput>
            </div>
            <div class="uk-width-1-2 uk-margin-bottom">
                <label>Funding request</label>
                <TextInput
                        class="uk-width-1-1 uk-input uk-form-large"
                        placeholder="CDBG-CV funding request"
                        rules="required"
                        :value="fundingRequested | formatMoney"
                        disabled
                ></TextInput>
            </div>
        </div>


        <h2>Proposed Itemized Activity Budget</h2>

        <div class="uk-grid" v-for="type in assets.unitLedgerEntryTypes" v-if="ledgerEntriesForType(type.id).length > 0">
            <div class="uk-width-1-1">
                <h3>{{ type.name.toUpperCase() }} ASSISTANCE REQUEST</h3>
                <table class="uk-table uk-table-striped">
                    <thead>
                    <tr>
                        <th>Funding Source</th>
                        <th v-for="(value, month) in housingSources.program.months">{{ month | moment('MMM YYYY') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td  style="padding-top: 15px;">
                            <span style="font-size: 1.3em; font-weight: bold;">{{ application.program.program_abbreviation }} Request:</span>
                        </td>
                        <td width="300px">
                            <vue-numeric disabled currency="$" :value="amountRequestedForUnitLedgerEntryType(type.id)"  class="uk-width-1-1 uk-input uk-form-large"/>
                        </td>

                    </tr>
                    <tr v-for="program in otherFundingTotalsForEntryTypeId(type.id)">
                        <td>
                            <div>
                                {{ program.name }} <i v-if="program.is_duplicative === 1"><small>(Duplicative Benefits)</small></i><i v-else><small>(Non-Duplicative Benefits)</small></i>
                            </div>
                        </td>
                        <td width="300px">
                            <vue-numeric currency="$"  :disabled="true" :value="program.amount" class="uk-width-1-1 uk-input uk-form-large"/>
                        </td>

                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="uk-grid">
            <div class="uk-width-1-1">
                <div class="uk-block uk-block-default">
                    <p>
                        Under penalties of perjury, I/we certify that the information presented in this document is true and accurate to the best of my knowledge and belief. I/We further understand that providing false representations herein constitutes an act of fraud. False, misleading or incomplete information may result in my ineligibility to participate in this program or any other programs that will accept this document. Additionally, if I/we receive future funding for the same purpose of the MRAP funds received, I/we will agree to repay the assistance that was duplicated. Warning: Any person who knowingly makes a false claim or statement to the U.S. Department of Treasury may be subject to civil or criminal penalties under 18 U.S.C. 287, 1001 and 31 U.S.C. 3729.
                    </p>
                </div>
            </div>
        </div>

        <div class="uk-grid" v-if="applicant">
            <div class="uk-width-small-1-1 uk-width-medium-1-1 uk-width-large-1-2">
                <validated-section :field="'signature_'+applicant.id" :errors="errors">
                    <signature-box @updated="onSignatureUpdated"
                                   :user-key="applicant.id"
                                   :is-signed="signatureData[applicant.id] && signatureData[applicant.id].signed_at !== null"
                                   :completed="signatureData[applicant.id] && signatureData[applicant.id].completed"
                                   :name="applicant.first_name + ' ' + applicant.last_name"></signature-box>
                </validated-section>
            </div>
        </div>

        <hr>

        <div class="footnotes">
            <ul>
                <li> <sup>1.</sup>  Eligible activities are: Rent Assistance, Utility Assistance and/or certain other expenses related to housing incurred due, directly or indirectly, to the COVID-19 outbreak.</li>
                <li> <sup>2.</sup>  “Total Need” is the total proposed activity cost. All costs included in total need must be reasonable and necessary. Applicant must provide applicable supporting documentation.</li>
                <li> <sup>3.</sup>  Not including MRAP. “Assistance” includes resources such as cash awards, insurance proceeds, grants, and loans received or anticipated by the MRAP applicant household, including awards under local, state or federal programs, and from private or nonprofit charity organizations. “Anticipated” assistance means assistance likely to be received by acting reasonably to evaluate need and the resources available to meet that need. Applicant must provide applicable supporting documentation for any source of funding cited in the total assistance calculation.</li>
                <li> <sup>4.</sup>  Assistance is non-duplicative if it is 1) provided for a different purpose; or 2) Provided for the same purpose (eligible activity), but for a different, allowable use (cost).</li>
                <li> <sup>5.</sup>  Total DOB equals “Total Assistance Received or Anticipated for Proposed Activity” minus “Total of Non-Duplicative Assistance.”</li>
                <li> <sup>6.</sup>  The MRAP funding request may not exceed the “Total Activity Cost” minus the “Total Duplication of Benefits (DOB).”</li>
                <li> <sup>7.</sup>  Add or delete columns or rows, as needed.</li>
            </ul>
        </div>
    </div>
</template>

<script>
    import IncomeEntryForm from "../../../../../components/AppWizard/Step3/IncomeEntryForm";
    import { mapState } from 'vuex';
    import ValidatedSection from "../../../../../components/Common/ValidatedSection";
    import SignatureBox from "../../../../../components/AppWizard/SignatureBox";

    export default {
        name: "DuplicativeBenefitsForm",
        components: {SignatureBox, ValidatedSection, IncomeEntryForm},
        props: {
            print: {
                default: false,
            }
        },
        data() {
            return {
                signatureData: {},
                errors: {},

                receivingAssistance: false,

                totalNeed: 0,
                totalReceivedOrAnticipated: 0,
                totalNonDuplicative: 0,
                totalDuplication: 0,
                cdbgFundingRequest: 0,

                housingSources: {
                    program: { name: '', months: {} },
                    '1': { duplicated: false, name: '', months: {}, allowUserEntry: true,},
                    '2': { duplicated: false, name: '', months: {}, allowUserEntry: true,},
                    '3': { duplicated: false, name: '', months: {}, allowUserEntry: true,},
                    '4': { duplicated: false, name: '', months: {}, allowUserEntry: true,},
                },

                utilitySources: {
                    program: { name: '', months: {} },
                    '1': { duplicated: false, name: '', months: {}, allowUserEntry: true, },
                    '2': { duplicated: false, name: '', months: {}, allowUserEntry: true, },
                    '3': { duplicated: false, name: '', months: {}, allowUserEntry: true, },
                    '4': { duplicated: false, name: '', months: {}, allowUserEntry: true, },
                }
            }
        },
        watch: {
            application(newValue) {
                if(newValue.household) {
                    this.initData();
                }
            }
        },
        computed: {
            fullAddress() {
                let address = this.applicant.property_address.line_1;
                if(this.applicant.property_address.line_2) {
                    address += ' ' + this.applicant.property_address.line_2
                }

                address += ' ' + this.applicant.property_address.city
                address += ' ' + this.applicant.property_address.state
                address += ' ' + this.applicant.property_address.zip

                return address;
            },
            fundingRequested() {
                return this.requestedAmount - this.duplicatedAmount;
            },
            duplicatedAmount() {
                let amount = 0;
                _.each(this.unitLedgerEntries, entry => {
                    amount = parseFloat(amount + entry.other_funding_amount);
                    _.each(entry.alternative_funding_programs, program => {
                        if(program.is_duplicative) {
                            amount = parseFloat(amount + program.pivot.amount);
                        }
                    });
                });
                return amount;
            },
            nonDuplicatedAmount() {
                return this.requestedAmount - this.duplicatedAmount;
            },
            requestedAmount() {
                let amount = 0;

                _.each(this.unitLedgerEntries, entry => {
                    amount += this.amountRequestedForEntry(entry);
                })

                return amount;
            },

            /**
             * Amount of funding from other sources
             * @returns {number}
             */
            anticipatedAmount() {
                let amount = 0;
                _.each(this.unitLedgerEntries, entry => {
                    amount += parseFloat(entry.other_funding_amount);
                })
                return amount;
            },
            hasHouseholdEarners() {
                let hasEarners = false;
                if(this.primaryApplicantHasIncome) {
                    hasEarners = true;
                }
                for(let i in this.householdMembersWithIncome) {
                    let member = this.householdMembersWithIncome[i];
                    console.log(member);
                    if(member.hasIncome) {
                        hasEarners = true;
                    }
                }
                return hasEarners;
            },
            household() {
                if(this.application && this.application.household) {
                    return this.application.household;
                }
            },
            ...mapState('Application', {
                assets: state => state.assets,
                application: state => state.application,
                applicant: state => state.applicant,
                alternativeFundingProgrms: state => state.assets.alternativeFundingPrograms,
                unitLedgerEntries: state => state.application.unit_ledger_entries,
                signatures: state => state.applicant.signatures,
            }),
        },
        mounted() {
            if(this.application) {
                this.initData();
            }
        },
        methods: {

            ledgerEntriesForType(typeId) {
                return _.filter(this.unitLedgerEntries, entry => entry.unit_ledger_entry_type_id === typeId);
            },

            amountRequestedForEntry(entry) {
                return parseFloat(entry.amount_due) + parseFloat(entry.fees_due) - parseFloat(entry.amount_paid);
            },

            amountRequestedForUnitLedgerEntryType(typeId) {
                let total = 0;
                _.each(this.ledgerEntriesForType(typeId), entry => {
                    total += this.amountRequestedForEntry(entry);
                })
                return total;
            },

            otherFundingTotalsForEntryTypeId(typeId) {
                let totals = {};

                _.each(this.ledgerEntriesForType(typeId), entry => {
                    if(entry.alternative_funding_programs.length === 0) return;
                    _.each(entry.alternative_funding_programs, program => {
                        if(totals[program.id] === undefined) {
                            totals[program.id] = {
                                name: program.name,
                                amount: 0,
                            }
                        }
                        totals[program.id].amount += parseFloat(program.pivot.amount);
                        totals[program.id].is_duplicative = program.is_duplicative;
                    })

                });

                return totals;
            },

            programForId(id) {
                let program = null;
                _.each(this.alternativeFundingPrograms, p => {
                    if(p.id === id) {
                        program = p;
                    }
                })
                return program;
            },
            initData() {
                const signatureFiltered = this.signatures.find(item => item.key === `app-duplicative`)
                if(signatureFiltered) {
                    this.$set(this.signatureData, this.applicant.id, { signed_at: signatureFiltered.created_at, completed: true });
                }

                // Build 'other' array of housing sources and utility sources based on
                // months in application where assistance was requested for that type
                let programLineRef = {}; // programId: lineIndex
                // let amounts = (this.application.home_owner) ? this.application.requested_mortgage_meta : this.application.requested_rent_meta
                // if(amounts.length > 0) {
                //     _.each(amounts, meta => {
                //         let fullDateString = meta.date+'-15';
                //         this.$set(this.housingSources.program.months, fullDateString, this.getRequestedAmount(meta));
                //         _.times(4, i => {
                //             // Check to see if a program is set
                //             if(meta.program_id && meta.program_id > 0) {
                //                 if(programLineRef[meta.program_id] === undefined && !_.includes(programLineRef, i)) {
                //                     programLineRef[meta.program_id] = i;
                //                 }
                //
                //                 if(programLineRef[meta.program_id] === i ) {
                //                     let program = this.programForId(meta.program_id);
                //                     this.$set(this.housingSources[i+1], 'name', program.name);
                //                     this.$set(this.housingSources[i+1], 'allowUserEntry', false);
                //                     this.$set(this.housingSources[i+1], 'duplicated', program.is_duplicative === 1);
                //                     this.$set(this.housingSources[i+1].months, fullDateString, meta.program_paid);
                //                 }else {
                //                     this.$set(this.housingSources[i+1].months, fullDateString, 0);
                //                 }
                //             } else if (meta.program_id === -1) {
                //                 console.log('-1 line ref', programLineRef, programLineRef[-1], programLineRef[i]);
                //                 if(programLineRef[-1] === undefined && !_.includes(programLineRef, i)) {
                //                     programLineRef[-1] = i;
                //                 }
                //
                //                 if(programLineRef[-1] === i) {
                //                     this.$set(this.housingSources[i+1], 'name', 'OTHER');
                //                     this.$set(this.housingSources[i+1], 'allowUserEntry', false);
                //                     this.$set(this.housingSources[i+1].months, fullDateString, meta.program_paid);
                //                 }else {
                //                     this.$set(this.housingSources[i+1].months, fullDateString, 0);
                //                 }
                //
                //             } else {
                //                 this.$set(this.housingSources[i+1].months, fullDateString, 0);
                //             }
                //         })
                //     })
                //     console.log('rent line ref', programLineRef);
                // }
                //
                // programLineRef = {};
                // if(this.application.requested_utilities_meta.length > 0) {
                //     _.each(this.application.requested_utilities_meta, meta => {
                //         let fullDateString = meta.date+'-15';
                //         this.$set(this.utilitySources.program.months, fullDateString, this.getRequestedAmount(meta));
                //         this.utilitySources.program.months[fullDateString] = this.getRequestedAmount(meta);
                //         console.log('water ref', programLineRef);
                //         _.times(4, i => {
                //             // Check to see if a program is set
                //             if(meta.program_id && meta.program_id > 0) {
                //                 if(programLineRef[meta.program_id] === undefined && !_.includes(programLineRef, i)) {
                //                     programLineRef[meta.program_id] = i;
                //                 }
                //
                //                 if(programLineRef[meta.program_id] === i ) {
                //                     let program = this.programForId(meta.program_id);
                //                     this.$set(this.utilitySources[i+1], 'name', program.name);
                //                     this.$set(this.utilitySources[i+1], 'allowUserEntry', false);
                //                     this.$set(this.utilitySources[i+1], 'duplicated', program.is_duplicative === 1);
                //                     this.$set(this.utilitySources[i+1].months, fullDateString, meta.program_paid);
                //                 }else {
                //                     this.$set(this.utilitySources[i+1].months, fullDateString, 0);
                //                 }
                //             } else if (meta.program_id === -1) {
                //                 console.log('-1 line ref', programLineRef, programLineRef[-1], programLineRef[i]);
                //                 if(programLineRef[-1] === undefined && !_.includes(programLineRef, i)) {
                //                     programLineRef[-1] = i;
                //                 }
                //
                //                 if(programLineRef[-1] === i) {
                //                     this.$set(this.utilitySources[i+1], 'name', 'OTHER');
                //                     this.$set(this.utilitySources[i+1], 'allowUserEntry', false);
                //                     this.$set(this.utilitySources[i+1].months, fullDateString, meta.program_paid);
                //                 }else {
                //                     this.$set(this.utilitySources[i+1].months, fullDateString, 0);
                //                 }
                //
                //             } else {
                //                 this.$set(this.utilitySources[i+1].months, fullDateString, 0);
                //             }
                //         })
                //     })
                //     console.log('water line ref', programLineRef);
                // }
            },
            getRequestedAmount(amounts) {
                return parseFloat(amounts.due) + parseFloat(amounts.fees) - parseFloat(amounts.paid);
            },
            onSignatureUpdated($event) {
                if(!this.signatureData[$event.key]) {
                    this.$set(this.signatureData, $event.key, {});
                }
                this.$set(this.signatureData, $event.key, { signed_at: $event.isSigned ? moment().format('YYYY-MM-DD HH:mm') : null});
            },
            async validateStep() {
                this.errors = {};

                if(!this.signatureData[this.applicant.id]) {
                    this.errors['signature_'+this.applicant.id] = {
                        errors: [this.applicant.first_name + ' ' + this.applicant.last_name + ' must sign this form as their name appears above.'],
                    };
                }else if(this.signatureData[this.applicant.id] && this.signatureData[this.applicant.id].signed_at == null) {
                    this.errors['signature_'+this.applicant.id] = {
                        errors: [this.applicant.first_name + ' ' + this.applicant.last_name + ' must sign this form as their name appears above.'],
                    };
                }
                let errorCount = _.size(this.errors);

                if(errorCount > 0) return false;


                // Server Side Validation
                try {
                    let response = await this.save();
                    return true;
                }catch(err) {
                    console.error(err);
                }
            },
            save() {
                let data = {
                    signatures: this.signatureData,
                    applied_heap_or_pipp: this.appliedHeapPipp,
                    currently_receiving_assistance: this.receivingAssistance,
                    housing_duplicative_funding: this.housingSources,
                    utility_duplicative_funding: this.utilitySources,
                }
                return axios.put(`/api/hope/applications/${this.application.id}/step-duplicative`, data)
            }

        }
    }
</script>
